<template>
  <div
    class="mx-2 md:mx-0 panel-container overflow-y-scroll w-full rounded shadow bg-white px-1 py-1"
  >
    <!-- START REPORT ORDERED -->
    <template v-if="getCurrentApplication && getCurrentReport">
      <!-- START REPORT PENDING -->
      <ReportPending />
      <!-- END REPORT PENDING -->

      <!-- START REPORT ERROR -->
      <template
        v-if="['error', 'error_user_action_required', 'fatal'].includes(getCurrentReport.status)"
      >
        <div class="flex flex-col md:w-4/5 mx-auto py-5 px-3 text-left">
          <template
            v-if="
              getCurrentReport.status === 'fatal' && getCurrentReport.report_type === 'identity'
            "
          >
            <ExclamationCircleIcon class="w-12 h-12 text-brand-red my-5 mx-auto" />
            <h2 class="text-center py-3">
              This ID Verification report was not completed by the applicant.
            </h2>

            <p class="py-5">
              We emailed the applicant with the necessary steps to complete this report but they
              have not responded. When they do not complete the verification within 5 days, the
              report is closed.
            </p>

            <p class="py-5">
              If you have further questions, please email
              <a class="text-brand-green" :href="`mailto:${supportEmail}`"> {{ supportEmail }}</a>
              for assistance.
            </p>
          </template>
          <template v-else>
            <template
              v-if="['missing_fields', 'invalid_address'].includes(getCurrentReport.error_type)"
            >
              <ExclamationIcon class="text-brand-yellow w-12 h-12 my-5 mx-auto" />
              <h3 class="text-center py-3">Report needs valid inputs to finish processing</h3>
            </template>
            <template v-else>
              <ExclamationCircleIcon class="w-12 h-12 text-brand-red my-5 mx-auto" />
              <h2 class="text-center py-3">There was an error while processing this report</h2>
            </template>

            <template v-if="getCurrentReport.error_type === 'viewer_not_auth'">
              <div class="flex flex-col px-3">
                <p class="py-2">
                  We were unable to provide this credit report because you need to re-verify your
                  identity with Experian.
                </p>
                <p class="py-2">
                  We know you've already verified your identity with Experian to be able to pull
                  credit reports, but Experian requires a periodic refresh of the credentials based
                  on the activity of the account.
                </p>
                <p class="py-2">
                  Once you have passed the verification process, this credit report will process
                  automatically.
                </p>

                <div class="flex md:w-1/4 mx-auto py-5">
                  <PrimaryButton @click="navigateToExperianVerification">Verify</PrimaryButton>
                </div>
              </div>
            </template>
            <template v-else-if="getCurrentReport.error_type === 'viewer_not_auth_as_consumer'">
              <div class="flex flex-col px-3">
                <p class="py-2">
                  We're waiting for your landlord to re-verify through Experian, similar to how you
                  did, before we can complete your report successfully.
                </p>
                <p class="py-2">
                  Since you're sharing some sensitive information, Experian must verify both parties
                  before allowing either of you to view the reports. I hope you understand the
                  inconvenience-- we prioritize the security of your identity and personal
                  information.
                </p>
                <p class="py-2">
                  We have contacted the property representative you're working with directly and as
                  soon as we hear back from them, we should be able to finish processing your
                  report! If you have additional questions, please contact
                  <a class="text-brand-green" href="mailto:support@rentapplication.net"
                    >support@rentapplication.net</a
                  >
                </p>
              </div>
            </template>
            <template v-else-if="getCurrentReport.error_type === 'consumer_not_auth_as_consumer'">
              <div class="flex flex-col px-3">
                <p class="py-2">
                  Your Experian credit report failed because you did not successfully pass
                  Experian's identity verification at the time you submitted your application.
                </p>
                <p class="py-2">
                  For security purposes, Experian requires you to verify your identity before
                  processing your credit report. To do so, you have to enter some information about
                  yourself and answer a few questions.
                </p>
                <p class="py-2">Please click the button below to verify:</p>

                <div class="flex md:w-1/4 mx-auto py-5">
                  <PrimaryButton @click="navigateToExperianVerificationApplicant"
                    >Verify</PrimaryButton
                  >
                </div>
              </div>
            </template>
            <template v-else-if="getCurrentReport.error_type === 'consumer_not_auth'">
              <div class="flex flex-col px-3">
                <p class="py-2">
                  Experian was unable to verify the identity of your applicant and as a result, we
                  were unable to retrieve their credit report successfully.
                </p>
                <p class="py-2">
                  We've already reached out directly to ask them to try to authenticate again and
                  then notify us once they are successful. As soon as they do so we'll reprocess the
                  report and you'll be notified when it's ready.
                </p>
                <p class="py-2">
                  Feel free to contact the applicant directly
                  <a
                    class="text-brand-green underline"
                    :href="`mailto:${getCurrentApplication.applicant.email}`"
                    >{{ getCurrentApplication.applicant.email }}</a
                  >
                  and ask them to re-attempt ID verification using the instructions we've already
                  sent them.
                </p>
              </div>
            </template>
            <template v-else-if="getCurrentReport.error_type === 'missing_fields'">
              <p class="py-2">
                We were unable to process this {{ getCurrentReport.short_label }} report due to some
                missing information.
              </p>
              <template v-if="getCurrentUserContext === 'applicant'">
                <p class="py-2 mb-3">
                  Please enter the following information to finish processing the reports:
                </p>
                <FieldDisplay
                  :field="field"
                  @update="updateFieldValue(field, $event)"
                  @validated_address="field.validated_address = $event"
                  :ref="field.name"
                  mode="edit"
                  v-for="(field, idx) in getCurrentApplication.report_missing_info_fields"
                  :key="idx"
                />

                <div class="flex md:w-1/2 mx-auto py-5">
                  <PrimaryButton
                    @click="updateApplication"
                    :disabled="isReportLoading || isApplicationLoading"
                    :is-loading="isReportLoading || isApplicationLoading"
                    >Update application</PrimaryButton
                  >
                </div>
              </template>
              <template v-else>
                <p class="py-5">Following required information of the application is missing:</p>
                <ol class="py-3 px-2">
                  <li v-for="field in getCurrentReport.missing_fields" :key="field">
                    {{ fieldNameMap[field] }}
                  </li>
                </ol>
                <p class="py-5">
                  We've also sent an email to you and your applicant about this error. As soon as
                  your applicant provides the necessary information, this report will process
                  automatically and you'll be notified. Please reach out to us if you have any
                  additional questions.
                </p>
              </template>
            </template>

            <template v-else-if="getCurrentReport.error_type === 'invalid_address'">
              <p class="py-2">
                We were unable to process this {{ getCurrentReport.short_label }} report due to
                invalid address.
              </p>
              <template v-if="getCurrentUserContext === 'applicant'">
                <p class="py-2 mb-3">
                  Please enter valid your current address to process this report:
                </p>

                <FieldDisplay
                  :field="field"
                  @update="updateFieldValue(field, $event)"
                  @validated_address="field.validated_address = $event"
                  :ref="field.name"
                  mode="edit"
                  v-for="(field, idx) in getCurrentApplication.report_missing_info_fields"
                  :key="idx"
                />

                <div class="flex md:w-1/2 mx-auto py-5">
                  <PrimaryButton
                    @click="updateApplication"
                    :disabled="isReportLoading || isApplicationLoading"
                    :is-loading="isReportLoading || isApplicationLoading"
                    >Update application</PrimaryButton
                  >
                </div>
              </template>
              <template v-else>
                <p class="py-3">
                  Applicant has entered an invalid address for their current address.
                </p>
                <p class="py-5">
                  We've also sent an email to you and your applicant about this error. As soon as
                  your applicant provides the necessary information, this report will process
                  automatically and you'll be notified. Please reach out to us if you have any
                  additional questions.
                </p>
              </template>
            </template>

            <template v-else>
              <template v-if="!errorReportSent">
                <p class="py-5">
                  We'll help you send a message to our support team to resolve this issue. Are there
                  any additional comments you'd like to share with us?
                </p>
                <MultiLineInput
                  placeholder="Add a message (optional)"
                  :value="errorReportMessage"
                  @input="errorReportMessage = $event.target.value"
                />
                <div class="flex md:w-1/2 mx-auto py-5">
                  <PrimaryButton
                    @click="sendErrorReport"
                    :disabled="isReportLoading"
                    :is-loading="isReportLoading"
                    >Send</PrimaryButton
                  >
                </div>
              </template>
              <template v-else>
                <p class="py-5">
                  Thanks! We will work to get this error resolved as quicky as possible and will
                  contact you if more information is needed.
                </p>
              </template>
            </template>
          </template>
        </div>
      </template>
      <!-- START REPORT ERROR -->

      <!-- START REPORT READY USER ACTION REQUIRED -->
      <template v-if="getCurrentReport.status === 'ready_user_action_required'">
        <template v-if="getCurrentUser.email !== getCurrentApplication.applicant">
          <div class="flex flex-col items-center md:w-2/3 mx-auto py-5 px-3 h-full">
            <template v-if="getCurrentReport.compliance_requirements === 'valid_unit_address'">
              <ExclamationCircleIcon class="text-brand-yellow w-10 h-10 my-5" />

              <h2 class="py-2">
                To view this report, please enter the property address this application is for.
              </h2>
              <p class="py-1">
                We were unable to identify the property address of the unit for this application.
              </p>
              <p>
                Due to varying state and local laws that place restrictions on the evaluation of
                {{ getCurrentReport.report_type }} records, we need the address to check if you're
                affected.
              </p>
              <p>
                If your property is located in an affected municipality, we'll reach out with
                additional information.
              </p>

              <div class="flex w-full md:w-2/3 py-3">
                <MultiLineInput
                  placeholder="123 Test Rd. Chicago, IL 60606"
                  :value="validAddress"
                  @input="validAddress = $event.target.value"
                />
              </div>

              <p v-if="errorMessage">{{ errorMessage }}</p>
              <PrimaryButton
                @click="fulfillRequirement('valid_unit_address')"
                :disabled="isReportLoading"
                :is-loading="isReportLoading"
                >Validate Property Address</PrimaryButton
              >
            </template>

            <template
              v-if="!isReportLoading && getCurrentReport.compliance_requirements === 'preapproval'"
            >
              <ExclamationIcon class="text-brand-yellow w-16 h-16" />
              <h2 class="py-2">Please read before proceeding</h2>
              <p class="py-2">
                This property appears to be located in
                <span class="font-semibold">{{ reportComplianceCriteria }}</span>
              </p>
              <p class="py-2">
                Legislation was passed that requires lessors of properties located in your
                jurisdiction to pre-qualify applicants on a financial basis before reviewing their
                criminal history.
              </p>
              <p class="py-2">
                When you agree to pre-qualify the applicant and request to review the criminal
                report, an email will be sent to the applicant to let them know they have been
                pre-qualified and their criminal report is under review.
              </p>
              <p class="py-2">
                To learn more about this legislation and your obligations as a lessor please
                <a
                  href="https://rent-application-faq.groovehq.com/help/fair-housing-compliance"
                  target="_blank"
                  class="text-brand-green"
                  >click here.</a
                >
              </p>
              <div class="flex flex-row items-center py-3">
                <input
                  id="preapproval"
                  type="checkbox"
                  class="rounded border-gray-300 text-brand-green focus:ring-brand-green w-4 h-4"
                  v-model="preapprovalConfirm"
                />
                <label for="preapproval" class="ml-2 block text-sm text-brand-black"
                  >I understand my obligation to comply with this legislation and pre-qualify this
                  applicant.</label
                >
              </div>
              <div class="py-3 flex">
                <PrimaryButton
                  @click="fulfillRequirement('preapproval')"
                  :disabled="isReportLoading"
                  :is-loading="isReportLoading"
                  >Show Report</PrimaryButton
                >
              </div>
              <p
                class="cursor-pointer text-brand-dark-gray py-3 text-sm underline"
                @click="openOptOutModal()"
              >
                Property not located in {{ reportComplianceCriteria }}?
              </p>
            </template>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col items-center md:w-2/3 mx-auto py-5 px-3 h-full">
            <ExclamationIcon class="text-brand-yellow w-16 h-16" />
            <h2 class="py-2">Your report is pending review</h2>
            <p class="py-2">
              This property appears to be located in
              <span class="font-semibold">{{ reportComplianceCriteria }}</span>
            </p>
            <p class="py-2">
              Legislation was passed that requires lessors of properties located in your
              jurisdiction to pre-qualify applicants on a financial basis before reviewing their
              criminal history.
            </p>
            <p class="py-2">
              When the lessor pre-qualifies your application and requests to review your criminal
              report, you will receive a notification email and will be able to review your report
              at that time.
            </p>
            <p class="py-2">
              If the property you applied to is not in {{ reportComplianceCriteria }}, the lessor
              will confirm the correct property address with us directly and your report will be
              available at that time.
            </p>
            <p class="py-2">
              To learn more about this legislation please
              <a
                href="https://rent-application-faq.groovehq.com/help/fair-housing-compliance"
                target="_blank"
                class="text-brand-green"
                >click here.</a
              >
            </p>
          </div>
        </template>
      </template>
      <!-- END REPORT READY USER ACTION REQUIRED -->

      <!-- START REPORT SENT PAYMENT REQUEST -->
      <template v-if="getCurrentReport.status === 'sent_payment_request'">
        <template v-if="getCurrentUserContext === 'applicant'">
          <div class="flex flex-col items-center md:w-2/3 mx-auto py-5 px-3">
            <h2 class="py-2">This report requires payment to begin processing</h2>
            <img src="@/assets/icons/moving-truck.svg" class="w-10 h-10 md:w-14 md:h-14" />
            <p class="py-2">
              The property representative you're working with has requested more information. Please
              complete the payment section below to begin processing.
            </p>

            <PrimaryButton @click="navigateToPayment">Pay</PrimaryButton>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col items-center md:w-2/3 mx-auto py-5 px-3">
            <h2 class="py-2">We've sent a request for payment to the applicant</h2>
            <img src="@/assets/icons/moving-truck.svg" class="w-10 h-10 md:w-14 md:h-14" />
            <p class="py-2">
              The report will be ordered once your applicant has submitted payment and you will be
              notified by email once the report is ready to view.
            </p>
          </div>
        </template>
      </template>
      <!-- END REPORT SENT PAYMENT REQUEST -->

      <!-- START REPORT READY -->
      <template
        v-if="
          getCurrentReport.status === 'ready' ||
          (getCurrentUser.email === getCurrentApplication.applicant &&
            getCurrentReport.status === 'ready_user_action_required')
        "
      >
        <template v-if="isReportLoading">
          <Loading />
        </template>

        <template v-if="!isReportLoading">
          <template v-if="getReportError">
            <div class="flex flex-col md:w-4/5 mx-auto py-5 px-3 text-left">
              <template v-if="getReportError.type === 'report_expired'">
                <div class="w-40 h-40 mx-auto">
                  <Lottie :animationData="animation" />
                </div>
                <h2 class="text-center py-5">Report Expired</h2>
                <template v-if="getCurrentReport.identifier === 'experian_credit'">
                  <p class="py-8">
                    Experian Credit reports are consumer-initiated reports and are only
                    valid/available to view for 30 days from when the report is first generated.
                  </p>

                  <p class="font-semibold pt-8 pb-3">From Experian:</p>
                  <p class="italic">
                    To protect your privacy, we keep copies of your credit report and score for 30
                    days only. If you need to keep a copy of your credit report for longer than 30
                    days, then you should print out your report. If you do print your report, you
                    should be sure to store it in a safe place.
                  </p>
                </template>
                <template v-else>
                  <p class="py-8 text-center">
                    This report has expired. For more information, please contact
                    <a class="text-brand-green" :href="`mailto:${supportEmail}`">
                      {{ supportEmail }}</a
                    >
                  </p>
                </template>
              </template>

              <template v-else-if="getReportError.type === 'auth_error'">
                <h2 class="text-center py-3">Authentication Error</h2>
                <p class="py-5">
                  The report provider has indicated that the credentials for accessing this report
                  are invalid or expired. If you believe this is an error, please contact
                  {{ supportEmail }}
                </p>
              </template>

              <template v-else-if="getReportError.type === 'invalid_enduser'">
                <h2 class="text-center py-3">Authentication Error</h2>
                <p class="py-5">
                  The report provider has indicated that the credentials for accessing this report
                  are invalid or expired. If you believe this is an error, please contact
                  {{ supportEmail }}
                </p>
              </template>

              <template v-else-if="getReportError.type === 'viewer_not_auth'">
                <h2 class="text-center py-3">Authentication Error</h2>
                <p class="py-5">
                  The report provider has indicated that the credentials for accessing this report
                  are invalid or expired. Please update your credentials
                  <a ui-sref="root.verifications.disclaimer({context: 'landlord'})"> here</a>
                </p>
              </template>

              <template v-else-if="getReportError.type === 'consumer_not_auth'">
                <h2 class="text-center py-3">Authentication Error</h2>
                <p class="py-5">
                  The report provider has indicated that the applicant has not yet completed their
                  identity verification. If you believe this is an error, please contact
                  {{ supportEmail }}.
                </p>
              </template>

              <template v-else-if="getReportError.type === 'viewer_not_auth_as_consumer'">
                <h2 class="text-center py-3">Authentication Error</h2>
                <p class="py-5">
                  The report provider has indicated that the credentials for landlord accessing this
                  report are invalid or expired. If you believe this is an error, please contact
                  {{ supportEmail }}.
                </p>
              </template>

              <template v-else-if="getReportError.type === 'consumer_not_auth_as_consumer'">
                <h2 class="text-center py-3">Authentication Error</h2>
                <p class="py-5">
                  Please retry completing your identity verification
                  <a ui-sref="root.applications.edit({ application_id: application.id })"> here.</a>
                </p>
              </template>

              <template
                v-else-if="['icred_empty_file', 'gvn_empty_file'].includes(getReportError.type)"
              >
                <h2 class="text-center py-3">Empty Credit File</h2>
                <p class="py-5">
                  The applicant's provided data did not match a credit file. This could mean that
                  the information provided was invalid or access is blocked by the applicant. If you
                  believe this is an error, please contact {{ supportEmail }}
                </p>
              </template>

              <template v-else-if="getReportError.type === 'invalid_country'">
                <h2 class="text-center py-3">Invalid Country</h2>
                <p class="py-5 text-center">
                  Experian credit reports can only be accessed within United States.
                </p>
              </template>

              <template v-else>
                <ExclamationCircleIcon class="w-12 h-12 text-brand-red my-5 mx-auto" />
                <h2 class="text-center py-3">There was an error while processing this report</h2>
                <template v-if="!errorReportSent">
                  <p class="py-5">
                    We’ll help you send a message to our support team to resolve this issue. Are
                    there any additional comments you’d like to share with us?
                  </p>
                  <MultiLineInput
                    placeholder="Add a message (optional)"
                    :value="errorReportMessage"
                    @input="errorReportMessage = $event.target.value"
                  />
                  <div class="flex md:w-1/2 mx-auto py-5">
                    <PrimaryButton
                      @click="sendErrorReport"
                      :disabled="isReportLoading"
                      :is-loading="isReportLoading"
                      >Send</PrimaryButton
                    >
                  </div>
                </template>
                <template v-else>
                  <p class="py-5">
                    Thanks! We will work to get this error resolved as quicky as possible and will
                    contact you if more information is needed
                  </p>
                </template>
              </template>
            </div>
          </template>

          <template v-if="!getReportError">
            <iframe
              class="w-full h-full"
              ref="iframe"
              :srcdoc="getCurrentReportHTML"
              frameborder="0"
              sandbox="allow-popups allow-scripts allow-same-origin"
            ></iframe>
          </template>
        </template>
      </template>
      <!-- END REPORT READY -->
    </template>
    <!-- END REPORT ORDERED -->

    <!-- START REPORT NOT ORDERED -->
    <template v-if="getCurrentApplication && !getCurrentReport && profile">
      <template v-if="getCurrentApplication.is_a_la_carte_eligible && !viewOnly">
        <!-- START EXPERIAN REPORT NOT ORDERED -->
        <template v-if="profile.source_identifier === 'experian_credit'">
          <div class="flex flex-col md:w-4/5 mx-auto py-5 px-3 text-left">
            <h2 class="py-3">
              Unfortunately Experian reports cannot be purchased after the application.
            </h2>
            <p class="py-1">
              The reason for this is because Experian requires the applicant to verify his or her
              identity, and if you were to buy it here, we wouldn't be able to do so.
            </p>
            <p class="py-1">
              If you want Experian credit reports, your application form must be configure in
              advance to automatically process a credit report or you must apply to verify your
              account through one of our alternate credit providers.
            </p>
            <p class="py-2">
              If you're interested in doing so, please email
              {{ supportEmail }} with the following information:
            </p>
            <p class="py-1">1. Your account email address</p>
            <p class="py-1">
              2. Whether you are a Landlord, Property Manager, or Real Estate Agent
            </p>
            <p class="py-1">
              3. How many credit reports have you processed in in the last 6 months?
            </p>
          </div>
        </template>
        <!-- END EXPERIAN REPORT NOT ORDERED -->

        <template v-else-if="profile.source_identifier.includes('credit') && isShareUser">
          <div class="flex flex-col md:w-4/5 mx-auto py-5 px-3 text-left">
            <h2 class="py-3">
              Unfortunately credit reports cannot be purchased after the application for sharees.
            </h2>
            <p class="py-1">
              If you want credit reports, please reach out to application owner to order the credit
              report.
            </p>
          </div>
        </template>

        <!-- START ORDER NON-EXPERIAN REPORT -->
        <template v-else>
          <template v-if="getCurrentApplication.landlord_can_edit">
            <template v-if="profile.can_buy_report">
              <div class="flex flex-col md:w-4/5 mx-auto py-5 px-3">
                <template v-if="showPaymentOptions">
                  <div class="flex flex-row py-5">
                    <div class="flex-1 flex">
                      <h2 class="text-xl md:text-3xl">
                        Order {{ profile.source_short_label }} Report
                      </h2>
                    </div>
                    <div class="flex items-center">
                      <LockClosedIcon class="text-brand-blue w-5 h-5 md:w-8 md:h-8 mr-1" />
                      <p class="text-brand-blue text-2xs md:text-xs">Secure</p>
                    </div>
                  </div>

                  <p class="py-5" v-if="!profile.is_async">
                    <template v-if="profile.source_identifier === 'mvga_employment_plus'">
                      The income verification section of the report will take 3-4 minutes to run.
                      The employment verification section of the report can take up to 3 business
                      days to complete. We'll notify you by email as soon as it's ready.
                    </template>
                    <template v-else>
                      The report will take 3-4 minutes to run. We'll notify you by email as soon as
                      it's ready or you can just refresh this page to view it in this tab.
                    </template>
                  </p>
                  <p class="py-5" v-if="profile.is_async">
                    The report can take up to 3 business days to complete. We'll notify you by email
                    as soon as it's ready.
                  </p>

                  <div class="py-5 flex flex-row">
                    <div class="hidden md:flex md:w-2/3"></div>

                    <div class="flex flex-row w-full md:w-1/3">
                      <div class="flex w-1/2">
                        <h4 class="">Total Fee</h4>
                      </div>
                      <div class="flex w-1/2 justify-end">
                        <h4 class="">${{ profile.price }}</h4>
                      </div>
                    </div>
                  </div>

                  <h4 class="py-5">Select a Payment Option:</h4>

                  <div class="flex flex-col rounded-lg border-2 border-brand-mid-gray">
                    <div class="py-2 px-3">
                      <div class="flex items-center py-1">
                        <label
                          for="landlord-pay"
                          class="inline-flex items-center cursor-pointer relative"
                          @mouseenter="profile.hover = true"
                          @mouseleave="profile.hover = false"
                        >
                          <input
                            name="landlord-pay"
                            id="landlord-pay"
                            type="radio"
                            class="w-7 h-7 border-gray-300 text-brand-green focus:ring-brand-green"
                            value="landlord"
                            v-model="paymentOption"
                            :disabled="
                              ['mvga_employment_plus', 'experian_credit'].includes(
                                profile.source_identifier
                              )
                            "
                          />
                          <span
                            class="ml-3 block"
                            :class="[
                              ['mvga_employment_plus', 'experian_credit'].includes(
                                profile.source_identifier
                              )
                                ? 'text-brand-mid-gray'
                                : 'text-brand-black'
                            ]"
                            >Pay report processing fee
                          </span>

                          <div
                            v-if="profile.source_identifier === 'mvga_employment_plus'"
                            :class="[
                              'max-w-64 py-2 px-2 text-xs bg-gray-600 text-gray-100 absolute top-10 left-5 px-1 rounded bg-opacity-80 shadow-xl',
                              profile.hover ? 'inline-block' : 'hidden'
                            ]"
                          >
                            Income+ Verification reports cannot be processed successfully via direct
                            payment due to the way this report is processed. You can send a request
                            for payment to the applicant directly.
                          </div>
                        </label>
                      </div>
                    </div>
                    <div class="py-2 px-3 border-t-2 border-brand-mid-gray">
                      <div class="flex items-center py-1">
                        <label for="applicant-pay" class="inline-flex items-center cursor-pointer">
                          <input
                            name="applicant-pay"
                            id="applicant-pay"
                            type="radio"
                            class="w-7 h-7 border-gray-300 text-brand-green focus:ring-brand-green"
                            value="applicant"
                            v-model="paymentOption"
                          />
                          <span class="ml-3 block text-brand-black"
                            >Request payment from applicant</span
                          ></label
                        >
                      </div>
                    </div>
                  </div>

                  <div class="my-8 w-full h-full md:w-1/2 mx-auto">
                    <PrimaryButton @click="togglePaymentOption()"> Order Report </PrimaryButton>
                  </div>
                </template>

                <template v-else>
                  <template v-if="paymentOption === 'landlord'">
                    <div class="flex items-center cursor-pointer" @click="setLandlordPaymentState">
                      <ChevronLeftIcon class="text-brand-green w-5 h-5 mr-1" />
                      <p>Back</p>
                    </div>
                    <div class="flex flex-row py-5">
                      <div class="flex-1 flex">
                        <h2 v-if="selectReports" class="text-xl md:text-3xl">
                          Select additional reports to order
                        </h2>
                        <h2 v-else>Order Report</h2>
                      </div>
                      <div class="flex items-center">
                        <LockClosedIcon class="text-brand-blue w-5 h-5 md:w-8 md:h-8 mr-1" />
                        <p class="text-brand-blue text-2xs md:text-xs">Secure</p>
                      </div>
                    </div>
                    <template v-if="selectReports">
                      <div class="flex flex-col">
                        <div class="flex items-center w-full">
                          <div class="flex items-center py-2">
                            <SecondaryButton @click="selectAll" :classes="'w-fit'"
                              >Select All</SecondaryButton
                            >
                          </div>
                        </div>
                        <div class="flex flex-col">
                          <div
                            class="flex flex-row border-2 border-mid-gray rounded py-5 px-5 my-1 justify-between"
                            v-for="(profile, index) in possibleProfiles"
                            :key="index"
                          >
                            <div class="flex items-center">
                              <input
                                :id="profile.source_identifier"
                                :name="profile.source_identifier"
                                type="checkbox"
                                class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
                                v-model="selectedProfiles[profile.source_identifier]"
                              />
                              <label :for="profile.source_identifier" class="ml-5 cursor-pointer">{{
                                profile.source_short_label
                              }}</label>
                            </div>
                            <div class="flex items-center">
                              <p>${{ profile.price }}</p>
                            </div>
                          </div>
                        </div>

                        <div class="py-5 flex flex-row">
                          <div class="hidden md:flex md:w-1/2"></div>

                          <div class="flex flex-row w-full md:w-1/2">
                            <div class="flex w-2/3">
                              <h4 class="">Total fee:</h4>
                            </div>
                            <div class="flex w-1/3 justify-end">
                              <h4 class="">${{ totalSelectedProfilesPrice }}</h4>
                            </div>
                          </div>
                        </div>

                        <div class="flex w-full md:w-1/2 md:mx-auto my-2 md:my-5">
                          <PrimaryButton @click="selectReports = false">
                            Order Report
                          </PrimaryButton>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <div class="flex flex-col">
                        <!-- TODO: Use saved card -->
                        <template
                          v-if="
                            getCurrentUser &&
                            getCurrentUser.landlord &&
                            getCurrentUser.landlord.card_on_file
                          "
                        >
                          <div class="flex flex-col items-center pb-5">
                            <label for="old-card" class="inline-flex items-center py-1">
                              <input
                                name="old-card"
                                id="old-card"
                                type="radio"
                                class="border-gray-300 text-brand-green focus:ring-brand-green"
                                value="old"
                                v-model="useCard"
                              />
                              <span class="ml-3 block text-brand-black flex items-center"
                                ><CustomCardIcon
                                  :type="getCurrentUser.landlord.card_on_file.brand"
                                />
                                ending in
                                {{ getCurrentUser.landlord.card_on_file.last4 }}</span
                              ></label
                            >
                            <label for="new-card" class="inline-flex items-center py-1">
                              <input
                                name="new-card"
                                id="new-card"
                                type="radio"
                                class="border-gray-300 text-brand-green focus:ring-brand-green"
                                value="new"
                                v-model="useCard"
                              />
                              <span class="ml-3 block text-brand-black"
                                >Use a different card</span
                              ></label
                            >
                          </div>
                        </template>
                        <template v-if="useCard === 'new'">
                          <CardInput class="py-2" ref="card"></CardInput>
                          <div class="flex items-center py-2 px-1">
                            <input
                              id="card-box"
                              type="checkbox"
                              class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
                              v-model="receipt"
                            />
                            <label for="card-box" class="ml-2 text-brand-dark-gray italic text-sm"
                              >I want to receive a receipt for this payment</label
                            >
                          </div>

                          <div class="flex items-center py-2 px-1">
                            <input
                              id="saveCard"
                              type="checkbox"
                              class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
                              v-model="saveCard"
                            />
                            <label for="saveCard" class="ml-2 text-brand-dark-gray italic text-sm"
                              >I want to save this card for future use</label
                            >
                          </div>
                        </template>

                        <div class="py-5 flex flex-row">
                          <div class="hidden md:flex md:w-2/3"></div>

                          <div class="flex flex-row w-full md:w-1/3">
                            <div class="flex w-1/2">
                              <h4 class="">Total Fee</h4>
                            </div>
                            <div class="flex w-1/2 justify-end">
                              <h4 class="">${{ totalSelectedProfilesPrice }}</h4>
                            </div>
                          </div>
                        </div>

                        <div class="flex md:w-1/3 md:mx-auto my-2 md:my-5">
                          <p v-if="errorMessage" class="text-brand-red mb-2 text-center">
                            {{ errorMessage }}
                          </p>
                          <PrimaryButton
                            @click="buyReport"
                            :disabled="isApplicationLoading"
                            :is-loading="isApplicationLoading"
                          >
                            Submit Payment
                          </PrimaryButton>
                        </div>
                      </div>
                    </template>
                  </template>

                  <template v-if="paymentOption === 'applicant'">
                    <div class="flex items-center cursor-pointer" @click="togglePaymentOption">
                      <ChevronLeftIcon class="text-brand-green w-5 h-5 mr-1" />
                      <p>Back</p>
                    </div>
                    <div class="flex flex-col">
                      <p class="py-5">
                        Send a request to the applicant to pay for the cost of the reports. Reports
                        will be ordered once the applicant submits payment.
                      </p>

                      <div class="flex items-center w-full">
                        <div class="flex items-center py-2">
                          <SecondaryButton @click="selectAll" :classes="'w-fit'"
                            >Select All</SecondaryButton
                          >
                        </div>
                      </div>

                      <div class="flex flex-col">
                        <div
                          class="flex flex-row border-2 border-mid-gray rounded py-5 px-5 my-1 justify-between"
                          v-for="(profile, index) in possibleProfiles"
                          :key="index"
                        >
                          <div class="flex items-center">
                            <input
                              :id="profile.source_identifier"
                              :name="profile.source_identifier"
                              type="checkbox"
                              class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
                              v-model="selectedProfiles[profile.source_identifier]"
                            />
                            <label :for="profile.source_identifier" class="ml-5 cursor-pointer">{{
                              profile.source_short_label
                            }}</label>
                          </div>
                          <div class="flex items-center">
                            <p>${{ profile.price }}</p>
                          </div>
                        </div>
                      </div>

                      <div class="py-5 flex flex-row">
                        <div class="hidden md:flex md:w-1/2"></div>

                        <div class="flex flex-row w-full md:w-1/2">
                          <div class="flex w-2/3">
                            <h4 class="">Total fee to applicant:</h4>
                          </div>
                          <div class="flex w-1/3 justify-end">
                            <h4 class="">${{ totalSelectedProfilesPrice }}</h4>
                          </div>
                        </div>
                      </div>

                      <div class="flex w-full md:w-1/2 md:mx-auto my-2 md:my-5">
                        <PrimaryButton
                          @click="sendPaymentRequest"
                          :disabled="isApplicationLoading"
                          :is-loading="isApplicationLoading"
                        >
                          Send Applicant Payment Request
                        </PrimaryButton>
                      </div>
                    </div>
                  </template>
                </template>
              </div>
            </template>

            <template v-else>
              <div class="flex flex-col md:w-4/5 mx-auto py-5 px-3">
                <h2 class="py-3">
                  Oops! Unfortunately, we are unable to order this
                  {{ profile.source_short_label }} report.
                </h2>
                <p class="py-3">
                  Normally we would be able to order this report based on the application
                  information that has already been submitted, but this form is missing the
                  following fields, which are required to request this report:
                </p>
                <div v-for="(field, index) in profile.missing_fields" :key="field">
                  <p class="py-1 text-sm px-3">{{ index + 1 }}. {{ fieldNameMap[field] }}</p>
                </div>
                <p class="py-3">
                  If you would like these fields back in to your Blank Application Form template,
                  please email {{ supportEmail }}
                </p>
              </div>
            </template>
          </template>
        </template>
        <!-- END ORDER NON-EXPERIAN REPORT -->
      </template>

      <template v-if="!getCurrentApplication.is_a_la_carte_eligible">
        <div class="flex flex-col items-center md:w-2/3 mx-auto py-5 px-3">
          <h2 class="py-3">
            Oops! Unfortunately, the applicant hasn't provided enough information for us to order
            this report.
          </h2>
          <p class="py-1">
            In order to run this report, your application form must include these fields:
          </p>
          <ul class="flex flex-col w-full py-2 px-2">
            <li class="text-sm text-left" v-for="field in requiredFields" :key="field">
              - {{ field }}
            </li>
          </ul>
          <p class="py-1">
            Either your form has been configured to allow the applicant to submit their application
            without one of these fields or your applicant has not included this information.
          </p>
          <p class="py-1">
            Unfortunately, we can't request the report without all of the required fields.
          </p>
        </div>
      </template>
    </template>
    <!-- END REPORT NOT ORDERED -->
  </div>
  <OptOutModal
    :criteria="reportComplianceCriteria"
    :showModal="showOptOutModal"
    @close="closeOptOutModal"
  />
</template>
<script>
import _ from "lodash"
import { mapGetters } from "vuex"

import Hourglass from "@/assets/animations/hourglass.json"

// eslint-disable-next-line no-unused-vars
import {
  REPORT_GET_HTML,
  REPORT_SET_CURRENT,
  REPORT_FULFILL_REQUIREMENT,
  APPLICATION_GET,
  APPLICATION_UPDATE,
  APPLICATION_BUY_REPORT,
  APPLICATION_SEND_PAYMENT_REQUEST,
  REPORT_SEND_ERROR_REPORT
} from "@/store/types"

import OptOutModal from "./components/OptOutModal.vue"
import ReportPending from "./components/ReportPending.vue"

import {
  ExclamationIcon,
  ExclamationCircleIcon,
  LockClosedIcon,
  ChevronLeftIcon
} from "@heroicons/vue/outline"

export default {
  components: {
    ExclamationIcon,
    ExclamationCircleIcon,
    LockClosedIcon,
    ChevronLeftIcon,
    OptOutModal,
    ReportPending
  },
  computed: {
    ...mapGetters([
      "getCurrentApplication",
      "getCurrentUser",
      "getCurrentReport",
      "getCurrentReportHTML",
      "isReportLoading",
      "isApplicationLoading",
      "getReportError",
      "getCurrentUserContext"
    ]),
    viewOnly() {
      return this.$route.name.includes("view_only")
    },
    isSample() {
      return this.$route.name.includes("home_demo")
    },
    reportComplianceCriteria() {
      if (!this.getCurrentReport) return ""

      if (this.getCurrentReport.compliance_requirements === "preapproval") {
        return `${
          this.getCurrentReport.compliance_criteria.county
            ? `${this.getCurrentReport.compliance_criteria.county} County`
            : ""
        }${
          this.getCurrentReport.compliance_criteria.city
            ? `, ${this.getCurrentReport.compliance_criteria.city}`
            : ""
        }${
          this.getCurrentReport.compliance_criteria.state
            ? `, ${this.getCurrentReport.compliance_criteria.state}`
            : ""
        }`
      }
      return ""
    },
    appFieldNames() {
      if (!this.getCurrentApplication || !this.getCurrentApplication.form) {
        return []
      }
      return this.getCurrentApplication.form.sections
        .map((section) => {
          return section.fields
            .map((field) => {
              if (field.field_id === "yes_no") {
                return [
                  field.name,
                  ...field.fields.map((f) => {
                    return f.name
                  })
                ]
              } else {
                return field.name
              }
            })
            .flat()
        })
        .flat()
    },
    possibleProfiles() {
      if (!this.getCurrentUser || !this.getCurrentApplication) return []
      return _.filter(
        this.getCurrentUser.report_source_profiles,
        (rsp) =>
          !_.find(
            this.getCurrentApplication.reports,
            (r) => r.identifier === rsp.source_identifier
          ) &&
          rsp.source_identifier !== "experian_credit" &&
          (this.paymentOption !== "landlord" || rsp.source_identifier !== "mvga_employment_plus")
      )
    },
    totalSelectedProfilesPrice() {
      return _.reduce(
        Object.keys(this.selectedProfiles),
        (total, k) => {
          return (
            total +
            (this.selectedProfiles[k]
              ? parseFloat(_.find(this.possibleProfiles, (p) => p.source_identifier === k).price)
              : 0)
          )
        },
        0
      ).toFixed(2)
    },
    isShareUser() {
      if (!this.getCurrentUser) {
        return false
      }

      return this.getCurrentApplication.shares
        .map((s) => s.shared_to.id)
        .includes(this.getCurrentUser.id)
    }
  },
  watch: {
    getCurrentApplication: {
      async handler(val) {
        if (val) {
          const report = _.find(
            this.getCurrentApplication.reports,
            (r) => r.report_type === this.type
          )
          this.$store.commit(REPORT_SET_CURRENT, report)

          if (!this.isSample && this.getCurrentUserContext !== "applicant") {
            this.setProfile()
            this.useCard =
              this.getCurrentUser &&
              this.getCurrentUser.landlord &&
              this.getCurrentUser.landlord.card_on_file
                ? "old"
                : "new"
          }
        }
      },
      deep: true,
      immediate: true
    },
    getCurrentReport: {
      async handler(val) {
        if (val) {
          if (val.status === "ready" && !this.getCurrentReportHTML) {
            await this.renderHTML()
          }
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.type = this.$route.params.type

    if (this.getCurrentApplication) {
      const report = _.find(this.getCurrentApplication.reports, (r) => r.report_type === this.type)
      this.$store.commit(REPORT_SET_CURRENT, report)

      // Only set profile for landlords, not applicants
      if (!this.isSample && this.getCurrentUserContext !== "applicant") {
        this.setProfile()

        if (this.profile) {
          this.selectedProfiles[this.profile.source_identifier] = true
        }
      }
    }
  },
  data() {
    return {
      animation: Hourglass,
      type: "",
      profile: null,
      requiredFields: [
        "First name",
        "Last name",
        "SSN",
        "Date of birth",
        "Address",
        "City",
        "State",
        "Zip Code"
      ],
      fieldNameMap: {
        applicant__first_name: "Applicant First Name",
        applicant__last_name: "Applicant Last Name",
        current_employer__supervisor_name: "Supervisor Name",
        current_employer__supervisor_phone: "Supervisor Phone #",
        current_employer__organization_name: "Organization Name",
        current_address__landlord_name: "Current Landlord Name",
        current_address__landlord_phone: "Current Landlord Phone #",
        previous_address__landlord_name: "Previous Landlord Name",
        previous_address__landlord_phone: "Previous Landlord Phone #",
        previous_address_1__landlord_name: "Previous Landlord Name",
        previous_address_1__landlord_phone: "Previous Landlord Phone #",
        previous_address_2__landlord_name: "Previous Landlord Name",
        previous_address_2__landlord_phone: "Previous Landlord Phone #",
        current_employer__work_email: "Applicant Work Email"
      },
      validAddress: "",
      errorMessage: "",
      preapprovalConfirm: false,
      showOptOutModal: false,
      paymentOption: "",
      receipt: true,
      saveCard: false,
      selectedProfiles: {},
      errorReportMessage: "",
      errorReportSent: false,
      showNotesModal: false,
      useCard: "new",
      showPaymentOptions: true,
      selectReports: true
    }
  },
  methods: {
    async updateApplication() {
      const isValid = _.every(
        this.getCurrentApplication.report_missing_info_fields.map((f) => {
          const el = Array.isArray(this.$refs[f.name]) ? this.$refs[f.name][0] : this.$refs[f.name]

          el.validateField()
          f.error = !el.isValid
          return el.isValid
        })
      )

      if (!isValid) {
        return
      }

      const report_missing_info_fields = this.getCurrentApplication.report_missing_info_fields

      await this.$store.dispatch(APPLICATION_UPDATE, {
        id: this.$route.params.id,
        data: { report_missing_info_fields }
      })
    },
    validateField(field) {
      if (field.field_id !== "text_display") {
        let fieldEl = this.getFieldRef(field)
        field.error = !fieldEl.isValid
        fieldEl.validateField()
      }
    },
    updateFieldValue(field, value) {
      field.value = value

      //Revalidate field if field is currently in error
      if (field.error) {
        this.validateField(field)
      }
    },
    selectAll() {
      this.possibleProfiles.forEach((p) => {
        this.selectedProfiles[p.source_identifier] = true
      })
    },
    setLandlordPaymentState() {
      if (!this.showPaymentOptions) {
        if (this.selectReports) {
          this.showPaymentOptions = true
        } else {
          this.selectReports = true
        }
      }
    },
    setProfile() {
      if (this.type) {
        // Use application owner's RSP on a la carte
        const profile = _.cloneDeep(
          _.find(
            this.getCurrentApplication.available_report_source_profiles,
            (rsp) => rsp.type === this.type
          )
        )

        if (profile) {
          let missingFields = _.difference(profile.required_fields, this.appFieldNames)

          if (
            missingFields.includes("current_address__address1") &&
            this.appFieldNames.includes("current_address__address")
          ) {
            const addressFields = [
              "current_address__address1",
              "current_address__city",
              "current_address__state",
              "current_address__zip_code"
            ]
            missingFields = missingFields.filter((f) => !addressFields.includes(f))
          }

          if (
            missingFields.includes("current_address__landlord_name") &&
            this.appFieldNames.includes("current_address__landlord")
          ) {
            missingFields = missingFields.filter(
              (f) =>
                !["current_address__landlord_name", "current_address__landlord_phone"].includes(f)
            )
          }

          if (
            missingFields.includes("previous_address__address1") &&
            this.appFieldNames.includes("previous_address__address")
          ) {
            const previousAddressFields = [
              "previous_address_1__address1",
              "previous_address_1__city",
              "previous_address_1__state",
              "previous_address_1__zip"
            ]
            missingFields = missingFields.filter((f) => !previousAddressFields.includes(f))
          }

          if (
            missingFields.includes("previous_address__landlord_name") &&
            this.appFieldNames.includes("previous_address__landlord")
          ) {
            missingFields = missingFields.filter(
              (f) =>
                !["previous_address__landlord_name", "previous_address__landlord_phone"].includes(f)
            )
          }

          // Handle legacy previous address fields
          if (
            missingFields.includes("previous_address__landlord_name") &&
            this.appFieldNames.includes("previous_address_1__landlord_name")
          ) {
            missingFields = missingFields.filter(
              (f) => !["previous_address__landlord_name"].includes(f)
            )
          }

          if (
            missingFields.includes("previous_address__landlord_phone") &&
            this.appFieldNames.includes("previous_address_1__landlord_phone")
          ) {
            missingFields = missingFields.filter(
              (f) => !["previous_address__landlord_phone"].includes(f)
            )
          }

          profile.can_buy_report = missingFields.length === 0
          profile.missing_fields = missingFields

          profile.is_async =
            profile.type.includes("landlord") || profile.type.includes("employment")

          this.profile = profile
        }
      }
    },
    async renderHTML() {
      try {
        await this.$store.dispatch(REPORT_GET_HTML)
      } catch (err) {
        console.log(err)
      }
    },
    async fulfillRequirement(action) {
      if (action === "preapproval" && !this.preapprovalConfirm) return
      const data = {
        action
      }

      if (this.validAddress) {
        data.detail = this.validAddress
      }
      try {
        await this.$store.dispatch(REPORT_FULFILL_REQUIREMENT, data)
        const id = this.$route.params.id
        await this.$store.dispatch(APPLICATION_GET, { id })
      } catch (err) {
        this.errorMessage =
          err.response.data.error ||
          "Unfortunately, an error has occured in processing this request. Please try again."
      }
    },
    openOptOutModal() {
      this.showOptOutModal = true
    },
    closeOptOutModal() {
      this.showOptOutModal = false
    },
    togglePaymentOption() {
      if (this.showPaymentOptions && !this.paymentOption) {
        return
      }
      this.showPaymentOptions = !this.showPaymentOptions
    },
    async buyReport() {
      const { receipt, saveCard } = this
      const identifiers = Object.keys(this.selectedProfiles)
        .map((k) => {
          if (this.selectedProfiles[k]) return k
        })
        .filter(Boolean)

      let stripeToken = null

      try {
        if (this.useCard === "new") {
          const { card, stripe } = this.$refs.card
          const result = await stripe.createToken(card)
          stripeToken = result.token.id
        }

        await this.$store.dispatch(APPLICATION_BUY_REPORT, {
          stripeToken,
          receipt,
          saveCard,
          identifiers
        })
      } catch (err) {
        console.log(err)
        this.errorMessage = err.response.data.message
      }
    },
    async sendPaymentRequest() {
      const identifiers = Object.keys(this.selectedProfiles)
        .map((k) => {
          if (this.selectedProfiles[k]) return k
        })
        .filter(Boolean)

      try {
        await this.$store.dispatch(APPLICATION_SEND_PAYMENT_REQUEST, {
          identifiers
        })
      } catch (err) {
        console.log(err)
      }
    },
    async sendErrorReport() {
      await this.$store.dispatch(REPORT_SEND_ERROR_REPORT, {
        message: this.errorReportMessage
      })
      this.errorReportMessage = ""
      this.errorReportSent = true
    },
    navigateToExperianVerification() {
      localStorage.setItem("redirect_after_verify", this.getCurrentApplication.id)
      this.$router.push({ name: "dashboard_verify_disclaimer" })
    },
    navigateToExperianVerificationApplicant() {
      this.$router.push({ name: "apply_verify", params: { id: this.getCurrentApplication.id } })
    },
    navigateToPayment() {
      this.$router.push({
        name: "request_payment_start",
        params: { id: this.getCurrentApplication.id }
      })
    }
  }
}
</script>
<style lang="scss" scoped>
.panel-container {
  min-height: 90vh;
  height: 100%;
}
@screen md {
  .panel-container {
    height: 700px;
  }
}
</style>
